import {useEffect, useRef} from 'preact/hooks';
import Sortable from 'sortablejs';

export function SortModeButton({sorting, disabled, label, onChange}) {
  return <s-button variant="tertiary" icon={sorting ? 'check' : 'sort'}
    accessibilityLabel={sorting ? `Done re-ordering ${label}` : `Re-order ${label}`}
    disabled={!sorting && disabled} onClick={() => onChange(!sorting)} />;
}

export function SortControls({index, count, label, disabled, onMove}) {
  const handle = useRef(null);
  const move = useRef(onMove);
  move.current = onMove;
  useEffect(() => {
    if (index !== 0 || disabled || count < 2) return;
    const body = handle.current.closest('s-table-body');
    const rows = [...body.querySelectorAll('[data-row-drag]')].map(h => h.closest('s-table-row'));
    // Sortable measures light-DOM hosts. Polaris renders the actual table boxes
    // inside shadow roots; forward geometry without changing table layout.
    const restore = [body, ...rows].map(element => {
      const previous = Object.getOwnPropertyDescriptor(element, 'getBoundingClientRect');
      element.getBoundingClientRect = () => element.shadowRoot.firstElementChild.getBoundingClientRect();
      return () => { if (previous) Object.defineProperty(element, 'getBoundingClientRect', previous); else delete element.getBoundingClientRect; };
    });
    rows.forEach(row => row.setAttribute('data-sortable-row', ''));
    let original = [];
    const sortable = Sortable.create(body, {
      draggable: '[data-sortable-row]', handle: '[data-row-drag]',
      direction: 'vertical', animation: 150, forceFallback: true,
      fallbackClass: 'evp-sortable-fallback', fallbackTolerance: 4,
      onChoose: () => { original = [...body.children]; },
      onEnd: ({oldDraggableIndex: from, newDraggableIndex: to}) => {
        // Restore DOM ownership before Preact applies the new array order.
        original.forEach(row => body.appendChild(row));
        if (Number.isInteger(from) && Number.isInteger(to) && from !== to) move.current(from, to);
      },
    });
    return () => { sortable.destroy(); restore.forEach(fn => fn()); rows.forEach(row => row.removeAttribute('data-sortable-row')); };
  }, [index, count, disabled]);
  return <>
    {index === 0 && <style>{'.evp-sortable-fallback{display:table!important;background:white;box-shadow:0 2px 12px #0003;opacity:.9;pointer-events:none;z-index:99999!important}'}</style>}
    <button ref={handle} type="button" data-row-drag aria-label={`Drag ${label} to reorder. Use Up or Down arrow keys to move.`}
      title="Drag to reorder" disabled={disabled || count < 2}
      style={{touchAction:'none', cursor:'grab', border:0, background:'transparent', color:'inherit', flexShrink:0, width:'24px', height:'28px', fontSize:'20px', padding:0}}
      onKeyDown={event => {
        const to = event.key === 'ArrowUp' ? index - 1 : event.key === 'ArrowDown' ? index + 1 : index;
        if (to !== index) { event.preventDefault(); if (to >= 0 && to < count) onMove(index, to); }
      }}><span aria-hidden="true">⠿</span></button>
  </>;
}
